import React, { useCallback, useEffect, useState } from "react";
import { Copy, Eye, EyeOff, Plus } from "lucide-react";
import { adminRouterUseCase } from "../../../../di/container";
import type { RouterEntity } from "../../../../domain/types/router";
import NotificationsList from "../../UI/NotificationList";
import { useNotifications } from "../../../hooks/use-notifications";

/**
 * 회사 상세 — 라우터 관리 탭.
 *
 * 구(Dev) 어드민의 "라우터 관리" 탭(라우터 목록 + 라우터 키 신규 발급)을
 * 현재 디자인 톤으로 포팅. 라우터별 IP / 마지막 장비 조회 / API 인증키(마스킹·복사)를 보여준다.
 */

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;
  return value;
};

interface RouterManagementTabProps {
  companyId: number | null;
}

const RouterManagementTab: React.FC<RouterManagementTabProps> = ({
  companyId,
}) => {
  const [routers, setRouters] = useState<RouterEntity[]>([]);
  const [visibleKeyIds, setVisibleKeyIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRouterName, setNewRouterName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const { notifications, addItem: addNotification } = useNotifications();

  const loadRouters = useCallback(async () => {
    if (companyId === null || Number.isNaN(companyId)) return;
    setIsLoading(true);
    try {
      const fetched = await adminRouterUseCase.getRouters(companyId);
      setRouters(fetched);
    } catch (error) {
      console.error("라우터 목록 조회 실패:", error);
      setRouters([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadRouters();
  }, [loadRouters]);

  const toggleKeyVisible = (routerId: number): void => {
    setVisibleKeyIds((prev) => {
      const next = new Set(prev);
      if (next.has(routerId)) next.delete(routerId);
      else next.add(routerId);
      return next;
    });
  };

  const copyApiKey = async (apiKey: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(apiKey);
      addNotification("API 인증키가 복사되었습니다.");
    } catch {
      addNotification("복사에 실패했습니다.");
    }
  };

  const handleCreate = async (): Promise<void> => {
    if (companyId === null || !newRouterName.trim() || isCreating) return;
    setIsCreating(true);
    try {
      await adminRouterUseCase.createRouter(companyId, {
        routerName: newRouterName.trim(),
      });
      addNotification("라우터 키가 발급되었습니다.");
      setIsModalOpen(false);
      setNewRouterName("");
      await loadRouters();
    } catch (error) {
      console.error("라우터 키 발급 실패:", error);
      addNotification("라우터 키 발급에 실패했습니다.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="p-5">
      <NotificationsList notifications={notifications} />

      {/* 신규 발급 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="relative bg-white rounded-[12px] p-6 w-full max-w-[420px] mx-4 shadow-lg">
            <h2 className="text-[16px] font-bold text-[#222] mb-4">
              라우터 키 신규 발급
            </h2>
            <label className="block text-[12px] text-[#555] mb-1">
              라우터 이름
            </label>
            <input
              type="text"
              value={newRouterName}
              onChange={(e) => setNewRouterName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
              placeholder="예: 본사-데이터수집기"
              autoFocus
              className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] mb-5"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#555] border border-[#D9D9D9] hover:bg-[#F9FAFB]"
              >
                취소
              </button>
              <button
                onClick={() => void handleCreate()}
                disabled={!newRouterName.trim() || isCreating}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#4285F4] text-white hover:bg-[#3367D6] disabled:opacity-40"
              >
                {isCreating ? "발급 중…" : "발급하기"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 헤더 + 발급 버튼 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[14px] font-semibold">
          라우터 목록{" "}
          <span className="text-[#888] font-normal">
            (전체 {routers.length}대)
          </span>
        </h3>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 font-medium rounded-lg px-3 py-2 text-[13px] bg-[#4285F4] text-white hover:bg-[#3367D6]"
        >
          <Plus size={15} />
          라우터 키 신규 발급
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-[12px] text-[#888]">
          불러오는 중…
        </div>
      ) : routers.length === 0 ? (
        <div className="py-10 text-center text-[12px] text-[#888]">
          발급된 라우터 키가 없습니다. 우측 상단에서 신규 발급해 주세요.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {routers.map((router) => {
            const isKeyVisible = visibleKeyIds.has(router.id);
            return (
              <div
                key={router.id}
                className="border border-[#D9D9D9] rounded-[10px] p-4 hover:bg-[#FAFAFA] transition-colors"
              >
                <h4 className="text-[14px] font-semibold mb-3">
                  {router.routerName || `라우터 #${router.id}`}
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-3">
                  <div>
                    <div className="text-[11px] text-[#888] mb-0.5">
                      IP 주소
                    </div>
                    <div className="text-[13px] font-medium">
                      {router.ipAddress || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-[#888] mb-0.5">
                      마지막 장비 조회
                    </div>
                    <div className="text-[13px] font-medium">
                      {formatDateTime(router.lastViewedAt)}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-[#888] mb-1">API 인증키</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 border border-[#D9D9D9] rounded-lg bg-[#F8F8F8] px-3 py-2 text-[13px] font-mono overflow-x-auto whitespace-nowrap">
                      {isKeyVisible
                        ? router.apiKey || "(비어 있음)"
                        : "**********************"}
                    </div>
                    <button
                      onClick={() => toggleKeyVisible(router.id)}
                      title={isKeyVisible ? "숨기기" : "보기"}
                      className="flex-shrink-0 w-9 h-9 rounded-lg border border-[#D9D9D9] bg-white hover:bg-[#F9FAFB] flex items-center justify-center text-[#555]"
                    >
                      {isKeyVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => void copyApiKey(router.apiKey ?? "")}
                      title="복사"
                      className="flex-shrink-0 w-9 h-9 rounded-lg border border-[#D9D9D9] bg-white hover:bg-[#F9FAFB] flex items-center justify-center text-[#555]"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RouterManagementTab;